import React, { useState } from 'react';
import Icon from '../AppIcon';

const SessionControls = ({ 
  onSave,
  onExit,
  onHelp,
  hasUnsavedChanges = false,
  lastSaved = null,
  isLoading = false 
}) => {
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showHelpPanel, setShowHelpPanel] = useState(false);

  const handleSave = () => {
    if (onSave && !isLoading) {
      onSave();
    }
  };

  const handleExit = () => {
    if (hasUnsavedChanges) {
      setShowExitConfirm(true);
    } else if (onExit) {
      onExit();
    }
  };

  const confirmExit = () => {
    setShowExitConfirm(false);
    if (onExit) {
      onExit();
    }
  };

  const cancelExit = () => {
    setShowExitConfirm(false);
  };

  const toggleHelp = () => {
    setShowHelpPanel(!showHelpPanel);
    if (onHelp) {
      onHelp(!showHelpPanel);
    }
  };

  const formatLastSaved = (timestamp) => {
    if (!timestamp) return null;
    const now = new Date();
    const saved = new Date(timestamp);
    const diffMinutes = Math.floor((now - saved) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return saved.toLocaleDateString();
  };

  return (
    <>
      {/* Session Controls Panel */}
      <div className="fixed top-20 right-4 z-60">
        <div className="bg-surface rounded-lg shadow-elevated border border-border p-3 space-y-2 w-48">
          {/* Save Status */}
          <div className="text-center pb-2 border-b border-border">
            <div className="flex items-center justify-center space-x-2 mb-1">
              <div className={`w-2 h-2 rounded-full ${hasUnsavedChanges ? 'bg-warning' : 'bg-success'}`} />
              <span className="text-xs font-caption text-text-secondary">
                {hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'}
              </span>
            </div>
            {lastSaved && (
              <p className="text-xs text-text-tertiary">
                Last saved: {formatLastSaved(lastSaved)}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={handleSave}
              disabled={isLoading || !hasUnsavedChanges}
              className="w-full flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} />
                  <span>Save Progress</span>
                </>
              )}
            </button>

            <button
              onClick={toggleHelp}
              className="w-full flex items-center space-x-2 px-3 py-2 bg-surface-secondary border border-border rounded-lg text-sm font-medium text-text-primary hover:bg-border-light transition-all duration-150"
            >
              <Icon name="HelpCircle" size={16} />
              <span>Get Help</span>
            </button>

            <button
              onClick={handleExit}
              className="w-full flex items-center space-x-2 px-3 py-2 bg-surface border border-error-100 text-error rounded-lg text-sm font-medium hover:bg-error-50 transition-all duration-150"
            >
              <Icon name="LogOut" size={16} />
              <span>Exit Assessment</span>
            </button>
          </div>
        </div>
      </div>

      {/* Help Panel */}
      {showHelpPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-70 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl shadow-modal border border-border max-w-md w-full max-h-96 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-text-primary">
                  Assessment Help
                </h3>
                <button
                  onClick={toggleHelp}
                  className="p-1 text-text-secondary hover:text-text-primary transition-colors duration-150"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-text-primary mb-2">How to complete your assessment</h4>
                  <ul className="text-sm text-text-secondary space-y-1">
                    <li>• Answer all questions honestly for accurate results</li>
                    <li>• Your progress is automatically saved</li>
                    <li>• You can return to previous sections to review</li>
                    <li>• Take breaks as needed - your session will be preserved</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-text-primary mb-2">Privacy & Security</h4>
                  <p className="text-sm text-text-secondary">
                    Your responses are encrypted and stored securely. Only you and authorized healthcare providers can access your results.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-text-primary mb-2">Need additional support?</h4>
                  <p className="text-sm text-text-secondary">
                    Contact our support team at support@healthassess.com or call 1-800-HEALTH for immediate assistance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-80 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl shadow-modal border border-border max-w-sm w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-warning-100 rounded-full flex items-center justify-center">
                  <Icon name="AlertTriangle" size={20} color="#F59E0B" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-text-primary">
                    Exit Assessment?
                  </h3>
                  <p className="text-sm text-text-secondary">
                    You have unsaved changes that will be lost.
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={cancelExit}
                  className="flex-1 px-4 py-2 bg-surface border border-border rounded-lg text-text-primary font-medium hover:bg-surface-secondary transition-all duration-150"
                >
                  Stay
                </button>
                <button
                  onClick={confirmExit}
                  className="flex-1 px-4 py-2 bg-error text-white rounded-lg font-medium hover:bg-red-600 transition-all duration-150"
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionControls;